import { useEffect, useRef, useState, useCallback } from "react";

/**
 * useGeolocation
 *
 * Polished + compliant:
 * - Permission state check (no silent re-prompt loops)
 * - Cached coords in localStorage
 * - Product-level consent choice: once | always | denied
 * - Explicit dialog control for UI surfaces (HOME “near you”)
 */
export default function useGeolocation(opts = {}) {
  const [status, setStatus] = useState("idle"); // idle | granted | denied | prompt | error | requesting
  const [coords, setCoords] = useState(null);
  const [error, setError] = useState("");

  // Consent decision (product preference; OS/browser permission is still separate)
  const CONSENT_KEY = "afrimed:geoConsent:v1"; // once | always | denied
  const [consent, setConsent] = useState(() => {
    try {
      return localStorage.getItem(CONSENT_KEY) || null;
    } catch {
      return null;
    }
  });

  // Dialog control (so wiring is not vague)
  const [dialogOpen, setDialogOpen] = useState(false);
  const openDialog = useCallback(() => setDialogOpen(true), []);
  const closeDialog = useCallback(() => setDialogOpen(false), []);

  const watchRef = useRef(null);

  const LS_KEY = "afrimed:lastCoords:v1";
  const GEO_OPTS = {
    enableHighAccuracy: true,
    timeout: 10000,
    maximumAge: 300000,
    ...opts,
  };

  const deniedMessage =
    "Finding your location is vital for nearest Healthcare providers to function correctly. You previously denied access. You can turn on location access in your device settings to use this feature.";

  const hasGeo =
    typeof navigator !== "undefined" &&
    "geolocation" in navigator &&
    (!("permissions" in navigator) || typeof navigator.permissions?.query === "function");

  // Load cached coords once
  useEffect(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.lat != null && parsed?.lng != null) setCoords(parsed);
      }
    } catch {}
  }, []);

  // Check permission without prompting
  useEffect(() => {
    let cancelled = false;

    async function checkPerm() {
      if (!("geolocation" in navigator)) {
        setStatus("error");
        setError("Geolocation not supported in this browser.");
        return;
      }

      // If Permissions API is missing, we can still request on user action.
      if (!("permissions" in navigator) || !navigator.permissions?.query) {
        setStatus(coords ? "granted" : "prompt");
        return;
      }

      try {
        const res = await navigator.permissions.query({ name: "geolocation" });
        if (cancelled) return;

        setStatus(res.state); // granted | denied | prompt

        // Auto grab if: already granted AND user chose "always" AND no coords yet
        if (res.state === "granted" && consent === "always" && !coords) {
          getOnce();
        }

        res.onchange = () => {
          if (cancelled) return;
          setStatus(res.state);
        };
      } catch (e) {
        setStatus("error");
        setError(e?.message || "Unable to read permission state.");
      }
    }

    checkPerm();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consent]);

  function persistConsent(v) {
    try {
      if (!v) localStorage.removeItem(CONSENT_KEY);
      else localStorage.setItem(CONSENT_KEY, v);
    } catch {}
    setConsent(v || null);
  }

  const getOnce = useCallback(() => {
    if (!("geolocation" in navigator)) return;

    setError("");
    setStatus("requesting");

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const next = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          acc: pos.coords.accuracy,
        };
        setCoords(next);
        try {
          localStorage.setItem(LS_KEY, JSON.stringify(next));
        } catch {}
        setStatus("granted");
      },
      (err) => {
        const msg = err?.message || "Location unavailable.";
        setError(err?.code === 1 ? deniedMessage : msg);

        if (err?.code === 1) setStatus("denied"); // PERMISSION_DENIED
        else setStatus("error");
      },
      GEO_OPTS
    );
  }, [GEO_OPTS]);

  function startWatch() {
    if (!("geolocation" in navigator)) return;
    stopWatch();

    watchRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        const next = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          acc: pos.coords.accuracy,
        };
        setCoords(next);
        try {
          localStorage.setItem(LS_KEY, JSON.stringify(next));
        } catch {}
        setStatus("granted");
      },
      (err) => {
        setError(err?.message || "Location watch failed.");
        if (err?.code === 1) setStatus("denied");
      },
      GEO_OPTS
    );
  }

  function stopWatch() {
    if (watchRef.current) {
      navigator.geolocation.clearWatch(watchRef.current);
      watchRef.current = null;
    }
  }

  // Consent actions (explicit)
  const allowOnce = useCallback(() => {
    persistConsent("once");
    closeDialog();
    getOnce();
  }, [closeDialog, getOnce]);

  const allowAlways = useCallback(() => {
    persistConsent("always");
    closeDialog();
    getOnce();
  }, [closeDialog, getOnce]);

  const deny = useCallback(() => {
    persistConsent("denied");
    closeDialog();
    setStatus("denied");
    setError(deniedMessage);
  }, [closeDialog]);

  const needsConsent = consent == null; // not decided yet (best UX: ask once, only on HOME “near you”)

  return {
    // existing API (kept)
    status,
    coords,
    error,
    getOnce,
    startWatch,
    stopWatch,

    // consent additions (polish)
    consent, // once | always | denied | null
    needsConsent,
    dialogOpen,
    openDialog,
    closeDialog,
    allowOnce,
    allowAlways,
    deny,
    hasGeo,
  };
}